import { omit } from 'lodash-es';

import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { FormLabel } from '@components/form-label';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconAttachment } from '@components/icons/attachment';
import { IconQuestion } from '@components/icons/question';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { CloseButton, Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';
import { uuid } from '@utils/uuid';

import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { JobQuestionMultiLanguage } from 'types/jobs';
import { MultiLanguageTextProps } from 'types/languages';

import { useQuestionsidebar } from './question-type-sidebar';

export const useTextFileSidebar = () => {
  const sidebar = useSidebar(TextFileSidebar, {
    variant: 'LARGE',
  });
  return sidebar;
};

type SimpleQuestionSidebarVariantKey = 'one_line' | 'multi_line' | 'attachment';

type SimpleQuestionSidebarVariant = {
  [key in SimpleQuestionSidebarVariantKey]: {
    title: keyof typeof textQuestionLocale.en;
    description: keyof typeof textQuestionLocale.en;
    icon: React.ReactNode;
    dataCy?: string;
  };
};
const Form = createForm();

export type SimpleQuestionSidebarProps =
  | {
      variant: keyof typeof textType;
      languages: CareerSiteLanguageType[];
      question?: never;
      addQuestion?: (question: JobQuestionMultiLanguage) => void;
      editQuestion?: never;
    }
  | {
      variant: keyof typeof textType;
      languages: CareerSiteLanguageType[];
      question?: JobQuestionMultiLanguage;
      addQuestion?: never;
      editQuestion?: (editedQuestion: JobQuestionMultiLanguage) => void;
    };

const textType: SimpleQuestionSidebarVariant = {
  one_line: {
    title: 'shortAnswer',
    description: 'shortAnswerDescription',
    icon: <IconTextShort />,
    dataCy: 'short-answer-textfield',
  },
  multi_line: {
    title: 'paragraph',
    description: 'paragraphDescription',
    icon: <IconTextLong />,
    dataCy: 'paragraph-textfield',
  },
  attachment: {
    title: 'file',
    description: 'fileDescription',
    icon: <IconAttachment />,
    dataCy: 'attachment-textfield',
  },
};

type TextQuestionType = {
  multipleAttachments: boolean;
} & MultiLanguageTextProps;

const TextFileSidebar = (props: SimpleQuestionSidebarProps) => {
  const { variant, languages, question, addQuestion, editQuestion } = props;
  const { t } = useScopedTranslate(textQuestionLocale);
  const showQuestionSidebar = useQuestionsidebar();
  const formStore = useCreateFormStore();

  const isDirty = formStore.useIsDirty();

  const isEdit = editQuestion ? true : false;

  const values = formStore.useFormValues();
  const isChecked = values['multipleAttachments'];

  const createQuestion = (title: MultiLanguageTextProps, multiple: boolean) => {
    const newQuestion: JobQuestionMultiLanguage = {
      id: uuid(),
      title: title,
      type: variant,
      multiple: multiple,
      order: 0,
      required: false,
      hidden: false,
    };
    return newQuestion;
  };

  const handleSave = (data: TextQuestionType) => {
    const multiple = data.multipleAttachments;
    if (question) {
      if (multiple) {
        const editedQuestion = question;
        languages.forEach((language) => {
          editedQuestion.title[language] = data[language];
        });
        editedQuestion.multiple = data.multipleAttachments;
        editQuestion?.(editedQuestion);
      } else {
        const editedQuestion = question;
        editedQuestion.title = omit(data, ['multipleAttachments']);
        editQuestion?.(editedQuestion);
      }
    } else {
      const newQuestion = createQuestion(omit(data, ['multipleAttachments']), multiple);
      addQuestion?.(newQuestion);
    }
    hideSidebar();
  };

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack space={2} orientation="horizontal" align="center">
            <IconQuestion color="purple50" />
            <Sidebar.HeaderTitle title={isEdit ? t('titleEdit') : t('title')} />
          </Stack>
          <Stack space={2} orientation="horizontal" align="center">
            {!isEdit && (
              <Button
                iconLeft={<IconArrowLeft />}
                onClick={() => showQuestionSidebar({ languages, addQuestion })}
                text={t('back')}
                variant="ghost"
              />
            )}
            <CloseButton />
          </Stack>
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={6}>
          <div>
            <Stack space={4} orientation="horizontal" align="center">
              {textType[variant].icon}
              <Text text={t(textType[variant].title)} font="bold18" />
            </Stack>
            <Text text={t(textType[variant].description)} font="regular14" color="purple80" />
          </div>
          <Form store={formStore} onSubmit={(data) => handleSave(data)}>
            {languages &&
              languages.map((language) => (
                <Form.TextField
                  dataCy={textType[variant].dataCy}
                  key={`${language}`}
                  id={`${language}`}
                  labelIcon={languages.length > 1 ? FLAG_MAPPER_SMALL[language] : undefined}
                  label={
                    languages.length > 1
                      ? t('question') + ' • ' + language.toUpperCase()
                      : t('question')
                  }
                  validators={['required']}
                  defaultValue={question ? question?.title[language] : undefined}
                />
              ))}
            {variant === 'attachment' && (
              <div>
                <FormLabel text={t('allowMultiple')} />
                <Stack orientation="horizontal" justify="space-between">
                  {isChecked ? (
                    <Text text={t('yes')} color="purple50" />
                  ) : (
                    <Text text={t('no')} color="purple50" />
                  )}
                  <Form.Switch
                    id="multipleAttachments"
                    label="Allow multiple attachments"
                    labelVisible={false}
                    defaultValue={question?.multiple ?? false}
                  />
                </Stack>
              </div>
            )}
            <Stack space={2} orientation="horizontal" justify="end">
              <Form.Actions>
                <Button
                  variant="secondary"
                  text={t('cancel')}
                  onClick={() => {
                    formStore.actions.resetStore();
                    hideSidebar();
                  }}
                />
                <Form.Submit
                  variant="primary-blue"
                  type="submit"
                  text={question ? t('save') : t('title')}
                  isDisabled={!isDirty}
                  dataCy={`${textType[variant].dataCy}-submit-button`}
                />
              </Form.Actions>
            </Stack>
          </Form>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const textQuestionLocale = {
  en: {
    title: 'Add question',
    titleEdit: 'Edit question',
    shortAnswer: 'Text - Short answer',
    shortAnswerDescription: 'Small text form field for simple questions',
    paragraph: 'Text - Long answer',
    paragraphDescription: 'Large text form field for more detailed answers',
    file: 'Attachment - File',
    fileDescription: 'Ask for a single or multiple files with the application',
    allowMultiple: 'Allow multiple attachments',
    yes: 'Yes',
    no: 'No',
    question: 'Question',
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
  },
  is: {
    title: 'Bæta við spurningu',
    titleEdit: 'Breyta spurningu',
    shortAnswer: 'Texti - Stutt svar',
    shortAnswerDescription: 'Lítill textareitur fyrir einfaldar spurningar',
    paragraph: 'Texti - Langt svar',
    paragraphDescription: 'Stór textareitur fyrir lengri svör',
    file: 'Viðhengi - Hlaða upp skrá',
    fileDescription: 'Leyfa umsækjanda að hlaða upp einni eða fleiri skrám með umsókninni',
    allowMultiple: 'Leyfa margar skrár',
    yes: 'Já',
    no: 'Nei',
    question: 'Spurning',
    back: 'Til baka',
    cancel: 'Hætta við',
    save: 'Vista',
  },
};
